import React, { useEffect, useState } from "react";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Plus, Trash2, Eye, EyeOff, Upload, ExternalLink, Calendar } from "lucide-react";

const empty = { title: "", image_url: "", link_url: "", starts_at: "", ends_at: "", sort_order: 0 };

export const AdminAdBannersPanel = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/arnak/ad-banners");
      setBanners(data.banners || []);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(empty); setEditingId(null); };

  const editBanner = (b) => {
    setEditingId(b.banner_id);
    setForm({
      title: b.title || "",
      image_url: b.image_url || "",
      link_url: b.link_url || "",
      starts_at: (b.starts_at || "").substring(0, 16),
      ends_at: (b.ends_at || "").substring(0, 16),
      sort_order: b.sort_order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.image_url) { toast.error("Titre et image requis"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        sort_order: parseInt(form.sort_order, 10) || 0,
        starts_at: form.starts_at ? form.starts_at.replace("T", " ") + ":00" : null,
        ends_at: form.ends_at ? form.ends_at.replace("T", " ") + ":00" : null,
      };
      if (editingId) await api.post(`/admin/arnak/ad-banners/${editingId}/update`, payload);
      else await api.post(`/admin/arnak/ad-banners`, payload);
      toast.success(editingId ? "Bannière mise à jour" : "Bannière créée");
      reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur");
    } finally { setSubmitting(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "arnak");
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploadée");
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); }
  };

  const toggle = async (id) => {
    try {
      await api.post(`/admin/arnak/ad-banners/${id}/toggle`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette bannière ?")) return;
    try {
      await api.post(`/admin/arnak/ad-banners/${id}/delete`);
      toast.success("Supprimée");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-ad-banners-panel">
      <div>
        <h2 className="font-black text-2xl mb-1" style={{ fontFamily: "'Anton', sans-serif" }}>Bannières partenaires</h2>
        <p className="text-xs text-white/55">
          Affichées <strong>toutes les 6 annonces</strong> dans la marketplace ARNAK. Idéal pour vendre de l'espace publicitaire à des partenaires.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 space-y-3">
        <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-black">
          {editingId ? "✏️ Modifier la bannière" : "➕ Nouvelle bannière"}
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Titre / Marque *</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: Studio RADO Production"
              className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-yellow-400 focus:outline-none"
              data-testid="banner-title-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">URL de destination</label>
            <input
              value={form.link_url}
              onChange={(e) => setForm({ ...form, link_url: e.target.value })}
              placeholder="https://…"
              className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-yellow-400 focus:outline-none"
              data-testid="banner-link-input"
            />
          </div>
        </div>

        {/* Image */}
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Image (1200×400 conseillé) *</label>
          {form.image_url ? (
            <div className="relative aspect-[3/1] rounded-lg overflow-hidden bg-zinc-900 border border-white/10 mb-2">
              <img src={assetUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="absolute top-2 right-2 px-3 py-1.5 bg-black/70 hover:bg-red-500 text-white text-xs font-bold rounded transition">Changer</button>
            </div>
          ) : (
            <label className="block aspect-[3/1] border-2 border-dashed border-yellow-400/30 hover:border-yellow-400/70 bg-yellow-400/[0.04] rounded-lg cursor-pointer transition flex flex-col items-center justify-center">
              {uploading ? (
                <span className="text-sm text-yellow-300">Upload…</span>
              ) : (
                <>
                  <Upload size={24} className="text-yellow-400 mb-2" />
                  <span className="text-xs font-bold">Cliquer pour uploader</span>
                  <span className="text-[10px] text-white/45">JPG/PNG/WEBP</span>
                </>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
            </label>
          )}
          <input
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            placeholder="Ou colle une URL d'image…"
            className="w-full px-3 py-2 bg-white/[0.04] border border-white/10 rounded-lg text-xs focus:border-yellow-400 focus:outline-none mt-2"
            data-testid="banner-image-url-input"
          />
        </div>

        {/* Dates */}
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">
              <Calendar size={10} className="inline -mt-0.5 mr-1" />Début (optionnel)
            </label>
            <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-yellow-400 focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Fin (optionnel)</label>
            <input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-yellow-400 focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Ordre d'affichage</label>
            <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-yellow-400 focus:outline-none" />
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={submitting} className="flex-1 sm:flex-none px-6 py-3 rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-40 text-black text-xs font-black uppercase tracking-widest transition" data-testid="banner-save-btn">
            {submitting ? "…" : editingId ? "Mettre à jour" : <>+ Créer la bannière</>}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-widest transition">Annuler</button>
          )}
        </div>
      </form>

      {/* Liste */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-white/55 font-black mb-3">
          {banners.length} bannière{banners.length > 1 ? "s" : ""} configurée{banners.length > 1 ? "s" : ""}
        </h3>
        {loading ? (
          <p className="text-center text-white/40 py-8">Chargement…</p>
        ) : banners.length === 0 ? (
          <div className="text-center py-12 bg-white/[0.02] border border-white/5 rounded-2xl">
            <ImageIcon size={32} className="mx-auto text-white/15 mb-2" />
            <p className="text-sm text-white/55">Aucune bannière configurée pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {banners.map((b) => (
              <div key={b.banner_id} className={`flex items-center gap-3 p-3 rounded-xl border transition ${b.is_active ? "bg-white/[0.03] border-white/10" : "bg-red-500/5 border-red-500/20 opacity-60"}`} data-testid={`banner-row-${b.banner_id}`}>
                <div className="w-24 sm:w-32 aspect-[3/1] rounded bg-zinc-900 overflow-hidden shrink-0">
                  {b.image_url ? <img src={assetUrl(b.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20"><ImageIcon size={20} /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold truncate text-sm">{b.title}</p>
                  {b.link_url && <a href={b.link_url} target="_blank" rel="noreferrer" className="text-[10px] text-yellow-300 hover:text-yellow-200 inline-flex items-center gap-1 truncate"><ExternalLink size={9} />{b.link_url}</a>}
                  <p className="text-[10px] text-white/40 mt-0.5">
                    {b.starts_at ? `Du ${b.starts_at.substring(0, 10)}` : "Sans début"} · {b.ends_at ? `→ ${b.ends_at.substring(0, 10)}` : "sans fin"} · ordre {b.sort_order}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggle(b.banner_id)} className={`p-2 rounded-lg transition ${b.is_active ? "hover:bg-white/15 text-white/80" : "hover:bg-yellow-400/15 text-yellow-300"}`} title={b.is_active ? "Désactiver" : "Activer"} data-testid={`banner-toggle-${b.banner_id}`}>
                    {b.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                  <button onClick={() => editBanner(b)} className="px-3 py-2 text-[10px] uppercase tracking-widest font-black hover:bg-white/15 rounded-lg transition">Édit</button>
                  <button onClick={() => del(b.banner_id)} className="p-2 rounded-lg hover:bg-red-500/15 hover:text-red-300 transition" title="Supprimer" data-testid={`banner-delete-${b.banner_id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAdBannersPanel;
